import React, { useEffect, useState } from 'react';
import { ResponsiveModal } from '../../ui/ResponsiveModal';
import { CheckCircle, Mail, MessageCircle, Link2, Loader, AlertTriangle, Send, FileSignature, Copy, Check, BookmarkPlus } from 'lucide-react';
import { useSendProposal } from '../../../hooks/deals/useSendProposal';
import { getUserWhatsAppConfig } from '../../../lib/utils/getWhatsAppConfig';
import { parsePhoneWithPrefix } from '../../../lib/utils/phonePrefix';
import { WaContactSearchInput } from '../../whatsapp/WaContactSearchInput';
import { useClientStore } from '../../../lib/stores/clientStore';
import { useContractClientGate } from '../../../hooks/useContractClientGate';
import { QuoteLinesPicker, useQuoteLineSelection } from './QuoteLinesPicker';
import type { Deal, DealService, QuoteTemplate } from '../../../lib/types';

type TabType = 'signature-link' | 'email-pdf' | 'email-link' | 'whatsapp-pdf';

function SaveToClientButton({ show, saved, onSave }: { show: boolean; saved: boolean; onSave: () => void }) {
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={onSave}
      className={`mt-1 flex items-center gap-1 text-[11px] transition-colors ${
        saved ? 'text-[#1FA855]' : 'text-[#2383E2] hover:text-[#1a6cba]'
      }`}
    >
      {saved ? <Check className="w-3 h-3" /> : <BookmarkPlus className="w-3 h-3" />}
      {saved ? 'Salvato nel cliente!' : 'Salva nel cliente'}
    </button>
  );
}

interface SendProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: Deal;
  services: DealService[];
  settings: any;
  /** Template adatti ai servizi dell'offerta. */
  templates: QuoteTemplate[];
  /** Non adatti: nascosti dietro "mostra tutti". */
  unsuitableTemplates?: QuoteTemplate[];
  /** false = l'organizzazione vieta di usare template non adatti. */
  allowUnsuitable?: boolean;
}

const SendProposalModal: React.FC<SendProposalModalProps> = ({
  isOpen,
  onClose,
  deal,
  services,
  settings,
  templates,
  unsuitableTemplates = [],
  allowUnsuitable = true
}) => {
  const [mostraTuttiTemplate, setMostraTuttiTemplate] = useState(false);
  const nonAdattiVisibili = allowUnsuitable && mostraTuttiTemplate ? unsuitableTemplates : [];
  const templatesVisibili = [...templates, ...nonAdattiVisibili];
  const { sending, sendingChannel, generateSignatureLink, sendByEmailWithPDF, sendByEmailWithLink, sendByWhatsApp } = useSendProposal();
  const updateClient = useClientStore(state => state.updateClient);
  const contractGate = useContractClientGate();
  const [savedFields, setSavedFields] = useState<Set<string>>(new Set());

  // Template selection
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Tab state — default "signature-link" (Firma online: solo link, niente invio)
  const [activeTab, setActiveTab] = useState<TabType>('signature-link');

  // Email fields
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // WhatsApp fields
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappCaption, setWhatsappCaption] = useState('');

  // Signature link only
  const [generatedSignatureLink, setGeneratedSignatureLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Recapiti dove il sistema invierà automaticamente il PDF firmato dopo la firma
  // online (entrambi opzionali). Precompilati da deal.client, modificabili.
  const [signatureNotifyEmail, setSignatureNotifyEmail] = useState('');
  const [signatureNotifyPhonePrefix, setSignatureNotifyPhonePrefix] = useState('+39');
  const [signatureNotifyPhoneNumber, setSignatureNotifyPhoneNumber] = useState('');

  // Availability checks
  const [whatsappAvailable, setWhatsappAvailable] = useState<boolean | null>(null);

  // Servizi e prodotti da mettere nel PDF allegato (Email + PDF, WhatsApp).
  // Tutti a ogni apertura. I canali con link di firma firmano l'offerta intera.
  const lineSelection = useQuoteLineSelection(services, isOpen);
  const pdfChannel = activeTab === 'email-pdf' || activeTab === 'whatsapp-pdf';

  // Pre-fill data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Auto-select first template
      if (templates.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(templates[0].id);
      }

      // Pre-fill email
      setEmailTo(deal.client?.email || '');
      setEmailSubject(`Proposta - ${deal.title || ''}`);
      setEmailBody(getDefaultEmailBody());

      // Pre-fill WhatsApp
      const phone = deal.client?.whatsapp_number || deal.client?.phone || '';
      setWhatsappPhone(phone);
      setWhatsappCaption(getDefaultWhatsAppCaption());

      // Reset signature link state + precompila recapiti notifica dal cliente
      setGeneratedSignatureLink(null);
      setLinkCopied(false);
      setSavedFields(new Set());
      setSignatureNotifyEmail(deal.client?.email || '');
      const parsed = parsePhoneWithPrefix(deal.client?.whatsapp_number || deal.client?.phone || '');
      setSignatureNotifyPhonePrefix(parsed.prefix);
      setSignatureNotifyPhoneNumber(parsed.number);

      // Check WhatsApp availability
      checkWhatsAppAvailability();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function getDefaultEmailBody(): string {
    const clientName = deal.client?.business_name || deal.client?.full_name || 'Cliente';
    // Paragrafi separati da blocco <p> indipendente — il textarea converte in newline doppie
    // visivamente, e l'output HTML finale è ben formato per SendGrid.
    return `<p>Gentile ${clientName},</p>\n<p>In allegato trova la nostra proposta "${deal.title || ''}".</p>\n<p>Restiamo a disposizione per qualsiasi chiarimento.</p>\n<p>Cordiali saluti</p>`;
  }

  function getDefaultEmailBodyWithLink(): string {
    const clientName = deal.client?.business_name || deal.client?.full_name || 'Cliente';
    return `<p>Gentile ${clientName},</p>\n<p>Le inviamo la nostra proposta "${deal.title || ''}".</p>\n<p>Clicchi il bottone qui sotto per visualizzare e firmare la proposta:</p>\n<p>{{LINK_FIRMA}}</p>\n<p>Restiamo a disposizione per qualsiasi chiarimento.</p>\n<p>Cordiali saluti</p>`;
  }

  function getDefaultWhatsAppCaption(): string {
    return `Buongiorno, le invio la proposta "${deal.title || ''}". Restiamo a disposizione per qualsiasi chiarimento.`;
  }

  function markSaved(field: string) {
    setSavedFields(prev => new Set(prev).add(field));
    window.setTimeout(() => {
      setSavedFields(prev => { const next = new Set(prev); next.delete(field); return next; });
    }, 2000);
  }

  async function saveEmailToClient(email: string) {
    if (!email || !deal.client_id) return;
    await updateClient(deal.client_id, { email });
    markSaved('email');
  }

  async function savePhoneToClient(phone: string) {
    if (!phone || !deal.client_id) return;
    await updateClient(deal.client_id, { phone, whatsapp_number: phone });
    markSaved('phone');
  }

  async function checkWhatsAppAvailability() {
    try {
      await getUserWhatsAppConfig();
      setWhatsappAvailable(true);
    } catch {
      setWhatsappAvailable(false);
    }
  }

  // Switch email body content when tab changes
  useEffect(() => {
    if (activeTab === 'email-link') {
      setEmailBody(getDefaultEmailBodyWithLink());
    } else if (activeTab === 'email-pdf') {
      setEmailBody(getDefaultEmailBody());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
  const canSend = selectedTemplateId && !sending && !(pdfChannel && lineSelection.noneSelected);

  async function handleSend(skipGate = false) {
    if (!selectedTemplate) return;

    // GATE dati cliente per i canali che creano un link di firma (Firma online /
    // Email + Link). Se mancano campi obbligatori apre il modale di completamento;
    // al salvataggio richiama handleSend(true) per proseguire.
    const isSignatureChannel =
      (activeTab === 'signature-link' && !generatedSignatureLink) || activeTab === 'email-link';
    if (!skipGate && isSignatureChannel) {
      await contractGate.guard(deal.client_id, () => { void handleSend(true); }, {
        dealId: deal.id,
        clientName: deal.client?.business_name || deal.client?.full_name,
      });
      return;
    }

    // Firma online: genera solo il link, NON chiude il modal (utente deve copiarlo).
    // Email + telefono memorizzati nella signature_request: flusia-core dopo la
    // firma invierà automaticamente il PDF firmato a questi recapiti.
    if (activeTab === 'signature-link') {
      // Se il link è già generato il bottone funge da "Chiudi"
      if (generatedSignatureLink) {
        onClose();
        return;
      }
      const composedNotifyPhone = signatureNotifyPhoneNumber.trim()
        ? `${signatureNotifyPhonePrefix}${signatureNotifyPhoneNumber.replace(/\s+/g, '')}`
        : '';
      const url = await generateSignatureLink({
        deal,
        template: selectedTemplate,
        notificationEmail: signatureNotifyEmail,
        notificationPhone: composedNotifyPhone,
      });
      if (url) {
        setGeneratedSignatureLink(url);
        setLinkCopied(false);
      }
      return;
    }

    let success = false;

    if (activeTab === 'email-pdf') {
      if (!emailTo) { return; }
      success = await sendByEmailWithPDF({
        deal, services, settings, template: selectedTemplate,
        to: emailTo, subject: emailSubject, body: emailBody,
        serviceIds: lineSelection.serviceIds
      });
    } else if (activeTab === 'email-link') {
      if (!emailTo) { return; }
      success = await sendByEmailWithLink({
        deal, services, settings, template: selectedTemplate,
        to: emailTo, subject: emailSubject, body: emailBody
      });
    } else if (activeTab === 'whatsapp-pdf') {
      if (!whatsappPhone) { return; }
      success = await sendByWhatsApp({
        deal, services, settings, template: selectedTemplate,
        phone: whatsappPhone, caption: whatsappCaption,
        serviceIds: lineSelection.serviceIds
      });
    }

    if (success) {
      onClose();
    }
  }

  async function handleCopyLink() {
    if (!generatedSignatureLink) return;
    try {
      await navigator.clipboard.writeText(generatedSignatureLink);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  }

  const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
    { key: 'signature-link', label: 'Firma online', icon: <FileSignature className="w-4 h-4" /> },
    { key: 'email-pdf', label: 'Email + PDF', icon: <Mail className="w-4 h-4" /> },
    { key: 'email-link', label: 'Email + Link Firma', icon: <Link2 className="w-4 h-4" /> },
    { key: 'whatsapp-pdf', label: 'WhatsApp', icon: <MessageCircle className="w-4 h-4" /> },
  ];

  function getSendButtonLabel(): string {
    if (sending) {
      if (sendingChannel === 'signature-link') return 'Generando link...';
      if (sendingChannel === 'email-pdf') return 'Generando e inviando PDF...';
      if (sendingChannel === 'email-link') return 'Generando link e inviando...';
      if (sendingChannel === 'whatsapp-pdf') return 'Generando e inviando su WhatsApp...';
      return 'Invio in corso...';
    }
    if (activeTab === 'signature-link') {
      return generatedSignatureLink ? 'Chiudi' : 'Genera Link Firma';
    }
    if (activeTab === 'email-pdf') return 'Genera e Invia PDF';
    if (activeTab === 'email-link') return 'Genera Link e Invia';
    if (activeTab === 'whatsapp-pdf') return 'Invia su WhatsApp';
    return 'Invia';
  }

  return (
    <>
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Invia Proposta"
      size="lg"
    >
      <div className="space-y-5">
        {/* Template Selection */}
        <div>
          <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1.5">Template</label>
          {templatesVisibili.length === 0 ? (
            <div className="py-3 text-center rounded-md border border-[rgba(55,53,47,0.12)] dark:border-[#454B4E] bg-black/[0.02] dark:bg-white/[0.03]">
              <p className="text-[13px] text-[#6B6762] dark:text-[#9B9A97]">
                {unsuitableTemplates.length > 0
                  ? 'Nessun template adatto ai servizi di questa offerta. Modifica l\'ambito nelle Impostazioni preventivi.'
                  : 'Nessun template disponibile. Crea prima un template nelle Impostazioni.'}
              </p>
            </div>
          ) : (
            <div className="max-h-40 overflow-y-auto">
              <div className="space-y-1.5">
                {templatesVisibili.map((template) => (
                  <div
                    key={template.id}
                    className={`p-2.5 rounded-md cursor-pointer border transition-colors ${
                      selectedTemplateId === template.id
                        ? 'border-[#2383E2] bg-[#2383E2]/[0.04]'
                        : 'border-[rgba(55,53,47,0.12)] dark:border-[#454B4E] hover:bg-black/[0.02] dark:hover:bg-white/[0.03]'
                    }`}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-[13px] font-medium text-[#37352F] dark:text-white">
                          {template.name}
                          {nonAdattiVisibili.some((u) => u.id === template.id) && (
                            <span className="ml-2 text-[10px] font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                              non adatto ai servizi
                            </span>
                          )}
                        </h3>
                        {template.description && (
                          <p className="text-[11px] text-[#6B6762] dark:text-[#9B9A97]">{template.description}</p>
                        )}
                      </div>
                      {selectedTemplateId === template.id && (
                        <CheckCircle className="h-4 w-4 text-[#2383E2] flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {allowUnsuitable && unsuitableTemplates.length > 0 && (
            <button
              type="button"
              onClick={() => setMostraTuttiTemplate((v) => !v)}
              className="mt-1.5 text-[11px] text-[#6B6762] dark:text-[#9B9A97] hover:underline"
            >
              {mostraTuttiTemplate
                ? 'Nascondi i non adatti'
                : `Mostra tutti (${unsuitableTemplates.length} non adatti a questi servizi)`}
            </button>
          )}
        </div>

        {/* Channel Tabs */}
        <div className="flex gap-1 p-1 bg-black/[0.03] dark:bg-white/[0.05] rounded-lg overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[12px] font-medium rounded-md whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? 'bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white shadow-sm border border-[rgba(55,53,47,0.12)] dark:border-[#454B4E]'
                  : 'text-[#6B6762] dark:text-[#9B9A97] hover:text-[#37352F] dark:hover:text-white'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-3">
          {pdfChannel && (
            <QuoteLinesPicker
              lines={services}
              selected={lineSelection.selected}
              onChange={lineSelection.setSelected}
            />
          )}

          {/* Firma online: genera solo il link, l'utente lo copia */}
          {activeTab === 'signature-link' && (
            <>
              {!generatedSignatureLink ? (
                <>
                  <div>
                    <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">
                      Email per invio PDF firmato (opzionale)
                    </label>
                    <input
                      type="email"
                      value={signatureNotifyEmail}
                      onChange={(e) => setSignatureNotifyEmail(e.target.value)}
                      placeholder="cliente@esempio.com"
                      className="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors"
                    />
                    <SaveToClientButton
                      show={!!signatureNotifyEmail && signatureNotifyEmail !== (deal.client?.email || '')}
                      saved={savedFields.has('email')}
                      onSave={() => saveEmailToClient(signatureNotifyEmail)}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">
                      Telefono WhatsApp per invio PDF firmato (opzionale)
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={signatureNotifyPhonePrefix}
                        onChange={(e) => setSignatureNotifyPhonePrefix(e.target.value)}
                        className="px-2 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors"
                      >
                        <option value="+39">🇮🇹 +39</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+49">🇩🇪 +49</option>
                        <option value="+33">🇫🇷 +33</option>
                        <option value="+34">🇪🇸 +34</option>
                        <option value="+41">🇨🇭 +41</option>
                        <option value="+43">🇦🇹 +43</option>
                        <option value="+32">🇧🇪 +32</option>
                        <option value="+31">🇳🇱 +31</option>
                        <option value="+351">🇵🇹 +351</option>
                        <option value="+30">🇬🇷 +30</option>
                        <option value="+45">🇩🇰 +45</option>
                        <option value="+46">🇸🇪 +46</option>
                        <option value="+47">🇳🇴 +47</option>
                        <option value="+358">🇫🇮 +358</option>
                        <option value="+48">🇵🇱 +48</option>
                        <option value="+36">🇭🇺 +36</option>
                        <option value="+420">🇨🇿 +420</option>
                        <option value="+7">🇷🇺 +7</option>
                        <option value="+86">🇨🇳 +86</option>
                        <option value="+81">🇯🇵 +81</option>
                        <option value="+82">🇰🇷 +82</option>
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+61">🇦🇺 +61</option>
                        <option value="+64">🇳🇿 +64</option>
                        <option value="+55">🇧🇷 +55</option>
                        <option value="+52">🇲🇽 +52</option>
                        <option value="+54">🇦🇷 +54</option>
                        <option value="+27">🇿🇦 +27</option>
                      </select>
                      <WaContactSearchInput
                        value={signatureNotifyPhoneNumber}
                        onChange={setSignatureNotifyPhoneNumber}
                        onPickContact={(number) => {
                          const parsed = parsePhoneWithPrefix(number);
                          setSignatureNotifyPhonePrefix(parsed.prefix);
                          setSignatureNotifyPhoneNumber(parsed.number);
                        }}
                        placeholder="333 1234567 o cerca per nome"
                        className="flex-1"
                        inputClassName="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors"
                      />
                    </div>
                    <SaveToClientButton
                      show={!!signatureNotifyPhoneNumber && `${signatureNotifyPhonePrefix}${signatureNotifyPhoneNumber.replace(/\s+/g, '')}` !== (deal.client?.whatsapp_number || deal.client?.phone || '')}
                      saved={savedFields.has('phone')}
                      onSave={() => savePhoneToClient(`${signatureNotifyPhonePrefix}${signatureNotifyPhoneNumber.replace(/\s+/g, '')}`)}
                    />
                  </div>
                  <div className="flex items-start gap-2 rounded-md border border-[#2383E2]/25 bg-[#2383E2]/[0.06] px-3 py-2.5 text-[12px] text-[#37352F] dark:text-[#FFFFFFCF]">
                    <FileSignature className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#2383E2]" />
                    <span>Clicca <strong>Genera Link Firma</strong> per ottenere un URL univoco. Subito dopo che il cliente firma, il PDF firmato sarà inviato automaticamente ai recapiti qui sopra. Puoi anche copiare il link e condividerlo come preferisci.</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-2 rounded-md border border-[#1FA855]/25 bg-[#1FA855]/[0.06] px-3 py-2.5 text-[12px] text-[#37352F] dark:text-[#FFFFFFCF]">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#1FA855]" />
                    <span>Link generato. Copialo e condividilo a piacere — il cliente potrà firmare la proposta da qualsiasi browser.</span>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">URL di firma</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={generatedSignatureLink}
                        readOnly
                        onFocus={(e) => e.currentTarget.select()}
                        className="flex-1 px-3 py-2 text-[12px] border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-black/[0.02] dark:bg-white/[0.03] text-[#37352F] dark:text-white font-mono outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors ${
                          linkCopied
                            ? 'bg-[#1FA855] text-white'
                            : 'bg-[#2383E2] text-white hover:bg-[#1a6cba]'
                        }`}
                      >
                        {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {linkCopied ? 'Copiato!' : 'Copia'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Email tabs (email-pdf and email-link) */}
          {(activeTab === 'email-pdf' || activeTab === 'email-link') && (
            <>
              <div>
                <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">Destinatario</label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="email@cliente.com"
                  className="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors"
                />
                <SaveToClientButton
                  show={!!emailTo && emailTo !== (deal.client?.email || '')}
                  saved={savedFields.has('email')}
                  onSave={() => saveEmailToClient(emailTo)}
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">Oggetto</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Oggetto dell'email"
                  className="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">Messaggio</label>
                <textarea
                  value={emailBody
                    // Converti i <p> in newline DOPPIE (paragrafi) — prima di strippare i tag
                    .replace(/<\/p\s*>\s*<p[^>]*>/gi, '\n\n')
                    .replace(/<p[^>]*>/gi, '')
                    .replace(/<\/p\s*>/gi, '')
                    .replace(/<br\s*\/?\s*>/gi, '\n')
                    // Rimuovi eventuali altri tag residui
                    .replace(/<[^>]*>/g, '')
                    .trim()}
                  onChange={(e) => {
                    // Wrap ogni paragrafo (separato da newline) in <p>. Mantieni linee vuote
                    // come separatori — usa split su \n\n+ per paragrafi, \n singoli diventano <br>.
                    const htmlBody = e.target.value
                      .split(/\n{2,}/)
                      .filter(Boolean)
                      .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br/>')}</p>`)
                      .join('');
                    setEmailBody(htmlBody);
                  }}
                  rows={6}
                  className="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors resize-none"
                />
              </div>
              {activeTab === 'email-link' && (
                <div className="flex items-start gap-2 rounded-md border border-[#2383E2]/25 bg-[#2383E2]/[0.06] px-3 py-2 text-[12px] text-[#37352F] dark:text-[#FFFFFFCF]">
                  <Link2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#2383E2]" />
                  <span>Verrà generato un link univoco per la firma digitale e inserito nell'email.</span>
                </div>
              )}
              <div className="text-[11px] text-[#6B6762] dark:text-[#9B9A97]">
                L'email verrà inviata da <span className="font-medium">notifications@flusia.it</span>. Le risposte del cliente arriveranno alla tua email.
              </div>
            </>
          )}

          {/* WhatsApp tab */}
          {activeTab === 'whatsapp-pdf' && (
            <>
              {whatsappAvailable === false && (
                <div className="flex items-start gap-2 rounded-md bg-[#FFF8E1] dark:bg-amber-900/20 border border-[#FFE0A3] dark:border-amber-800 px-3 py-2.5 text-[12px] text-[#8A6D3B] dark:text-amber-200">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#B7791F]" />
                  <span>WhatsApp non risulta configurato per il tuo account. Vai in <strong>Impostazioni → WhatsApp</strong> per collegare un numero o chiedi all'amministratore dell'organizzazione di assegnartene uno.</span>
                </div>
              )}
              <div>
                <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">Numero di telefono</label>
                <WaContactSearchInput
                  value={whatsappPhone}
                  onChange={setWhatsappPhone}
                  onPickContact={(number) => setWhatsappPhone(number)}
                  placeholder="+39 333 1234567 o cerca per nome"
                  inputClassName="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors"
                  disabled={whatsappAvailable === false}
                />
                <SaveToClientButton
                  show={!!whatsappPhone && whatsappPhone !== (deal.client?.whatsapp_number || deal.client?.phone || '')}
                  saved={savedFields.has('phone')}
                  onSave={() => savePhoneToClient(whatsappPhone)}
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF] mb-1">Messaggio</label>
                <textarea
                  value={whatsappCaption}
                  onChange={(e) => setWhatsappCaption(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-[rgba(55,53,47,0.16)] dark:border-[#454B4E] rounded-md bg-white dark:bg-[#2F3437] text-[#37352F] dark:text-white focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors resize-none"
                  disabled={whatsappAvailable === false}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-3 border-t border-black/[0.06] dark:border-white/[0.08]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#37352F] dark:text-white rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
            disabled={sending}
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => handleSend()}
            disabled={!canSend || (activeTab === 'whatsapp-pdf' && whatsappAvailable === false)}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg flex items-center gap-2 transition-colors ${
              !canSend || (activeTab === 'whatsapp-pdf' && whatsappAvailable === false)
                ? 'bg-[#2383E2]/50 cursor-not-allowed'
                : 'bg-[#2383E2] hover:bg-[#1a6cba]'
            }`}
          >
            {sending ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : activeTab === 'signature-link' && generatedSignatureLink ? (
              <Check className="w-4 h-4" />
            ) : activeTab === 'signature-link' ? (
              <FileSignature className="w-4 h-4" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {getSendButtonLabel()}
          </button>
        </div>
      </div>
    </ResponsiveModal>
    {contractGate.modal}
    </>
  );
};

export default SendProposalModal;
